// Self-service trial ("Ücretsiz deneyin" on the landing page). The visitor
// names their company, accepts Terms + KVKK, then signs in with Microsoft;
// the backend (organizations/signup.py) opens the organization, the admin
// account and a trial whose 14 days only start with the first delivered
// signature. Shares the login page's look (LoginPage.css).
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '@fontsource/barlow-condensed/700.css';
import '@fontsource/barlow-condensed/800.css';
import '@fontsource/barlow-condensed/800-italic.css';
import { api, endpoints } from '../../config/api';
import { Button } from '../../components/ui';
import useBrandLogo from '../../hooks/useBrandLogo';
import { useT, apiErrorMessage } from '../../i18n';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import { InkBackdrop } from '../marketing/landing/InkBackdrop';
import { Cursor, EASE_OUT, MaskedHeading } from '../marketing/landing/primitives';
import { MicrosoftLogo } from './LoginPage';
import './LoginPage.css';
import './StartTrialPage.css';

const PORTRAIT = { y: 0.66, fit: 0.42 };

const messages = {
  homeAria: { tr: 'Martinci İmza anasayfa', en: 'Martinci home' },
  home: { tr: '← Anasayfa', en: '← Home' },
  kicker: { tr: 'Ücretsiz deneme', en: 'Free trial' },
  titleLines: {
    tr: ['14 gün', [{ text: 'ücretsiz.', className: 'lp-accent' }]],
    en: ['14 days', [{ text: 'free.', className: 'lp-accent' }]],
  },
  lead: {
    tr: 'Kredi kartı gerekmez. Kurulum için acele etmeyin: deneme süreniz, ilk imzanız bir e-postaya eklendiği anda başlar.',
    en: 'No credit card needed. Take your time with setup: your trial only starts when your first signature is added to an email.',
  },
  steps: {
    tr: [
      ['Microsoft 365’i bağlayın', 'Bir genel yönetici tek tıkla onay verir; personel listeniz otomatik gelir.'],
      ['İmzanızı tasarlayın', 'Hazır şablonlardan birini seçip markanıza uyarlayın.'],
      ['Outlook eklentisini dağıtın', 'Yönetim merkezinden birkaç dakikada tüm ekibe yüklenir — deneme o an başlar.'],
    ],
    en: [
      ['Connect Microsoft 365', 'A global admin approves once and your staff directory comes in automatically.'],
      ['Design your signature', 'Start from a ready-made template and make it yours.'],
      ['Deploy the Outlook add-in', 'A few minutes in the admin center rolls it out to everyone — that’s when the trial starts.'],
    ],
  },
  company: { tr: 'Şirket adı', en: 'Company name' },
  companyPlaceholder: { tr: 'Örn. Acme Lojistik A.Ş.', en: 'E.g. Acme Logistics Ltd' },
  errCompany: { tr: 'Şirket adını yazın.', en: 'Enter your company name.' },
  consent: {
    tr: () => <><Link to="/kullanim-sartlari" target="_blank">Kullanım Şartları</Link>’nı ve{' '}<Link to="/kvkk-aydinlatma-metni" target="_blank">KVKK Aydınlatma Metni</Link>’ni okudum, kabul ediyorum.</>,
    en: () => <>I have read and accept the{' '}<Link to="/kullanim-sartlari" target="_blank">Terms of Use</Link> and the{' '}<Link to="/kvkk-aydinlatma-metni" target="_blank">KVKK privacy notice</Link>.</>,
  },
  errConsent: { tr: 'Devam etmek için onaylamanız gerekiyor.', en: 'Please accept to continue.' },
  start: { tr: 'Microsoft ile başla', en: 'Start with Microsoft' },
  redirecting: { tr: 'Microsoft’a yönlendiriliyor…', en: 'Redirecting to Microsoft…' },
  workAccount: {
    tr: 'Şirketinizin Microsoft 365 iş hesabıyla giriş yapın. Kişisel Outlook/Hotmail hesapları desteklenmez.',
    en: 'Sign in with your company’s Microsoft 365 work account. Personal Outlook/Hotmail accounts aren’t supported.',
  },
  msError: { tr: 'Microsoft ile giriş şu anda başlatılamadı', en: 'Microsoft sign-in couldn’t be started right now' },
  haveAccount: { tr: 'Hesabınız var mı?', en: 'Already have an account?' },
  signIn: { tr: 'Giriş yapın →', en: 'Sign in →' },
  preferDemo: { tr: 'Önce görmek mi istersiniz?', en: 'Prefer a walkthrough first?' },
  bookDemo: { tr: 'Demo planlayın →', en: 'Book a demo →' },
  legalAria: { tr: 'Yasal', en: 'Legal' },
  legalLinks: {
    tr: [['/kvkk-aydinlatma-metni', 'KVKK'], ['/gizlilik-politikasi', 'Gizlilik'], ['/cerez-politikasi', 'Çerezler']],
    en: [['/kvkk-aydinlatma-metni', 'KVKK Notice'], ['/gizlilik-politikasi', 'Privacy'], ['/cerez-politikasi', 'Cookies']],
  },
};

export const StartTrialPage = () => {
  const [company, setCompany] = useState('');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [redirecting, setRedirecting] = useState(false);
  const { t, lang } = useT(messages);
  const logo = useBrandLogo('white');

  const start = async (e) => {
    e.preventDefault();
    const er = {};
    if (company.trim().length < 2) er.company = t('errCompany');
    if (!consent) er.consent = t('errConsent');
    setErrors(er);
    if (Object.keys(er).length) return;

    setError('');
    setRedirecting(true);
    try {
      const res = await api.get(endpoints.auth.ssoMicrosoftLoginUrl, {
        params: { signup: '1', terms: '1', company: company.trim(), lang },
      });
      window.location.href = res.authorize_url;
    } catch (err) {
      setError(apiErrorMessage(err, messages.msError));
      setRedirecting(false);
    }
  };

  return (
    <div className="auth" lang={lang}>
      <Cursor />
      <div className="lp-grain" aria-hidden="true" />
      <InkBackdrop morph={redirecting ? 3 : 2} portrait={PORTRAIT} />

      <header className="auth-top">
        <Link to="/" className="auth-brand" aria-label={t('homeAria')}>
          <img src={logo.src} alt={logo.alt} />
        </Link>
        <div className="auth-top-actions">
          <LanguageSwitcher />
          <Link to="/" className="auth-back">{t('home')}</Link>
        </div>
      </header>

      <main className="auth-main">
        <motion.section
          className="auth-card trial-card"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE_OUT, delay: 0.15 }}
        >
          <span className="auth-kicker">{t('kicker')}</span>
          <MaskedHeading as="h1" immediate delay={0.3} className="auth-title" key={lang} lines={t('titleLines')} />
          <p className="auth-lead">{t('lead')}</p>

          <ol className="trial-steps">
            {t('steps').map(([title, text], i) => (
              <li key={title}>
                <span className="trial-step-num" aria-hidden="true">{i + 1}</span>
                <span><strong>{title}</strong> {text}</span>
              </li>
            ))}
          </ol>

          <form onSubmit={start} noValidate>
            <div className={`trial-field ${errors.company ? 'has-error' : ''}`}>
              <label htmlFor="trial-company">{t('company')}</label>
              <input
                id="trial-company" value={company} maxLength={200} autoComplete="organization"
                placeholder={t('companyPlaceholder')}
                onChange={(e) => { setCompany(e.target.value); if (errors.company) setErrors((x) => ({ ...x, company: undefined })); }}
              />
              {errors.company && <span className="trial-field-error">{errors.company}</span>}
            </div>

            <label className={`trial-consent ${errors.consent ? 'has-error' : ''}`}>
              <input
                type="checkbox" checked={consent}
                onChange={(e) => { setConsent(e.target.checked); if (errors.consent) setErrors((x) => ({ ...x, consent: undefined })); }}
              />
              <span>{t('consent')}</span>
            </label>
            {errors.consent && <span className="trial-field-error">{errors.consent}</span>}

            {error && <div className="auth-error" role="alert"><p>{error}</p></div>}

            <Button type="submit" variant="microsoft" className="auth-ms-btn trial-submit" disabled={redirecting}>
              <MicrosoftLogo />
              {redirecting ? t('redirecting') : t('start')}
            </Button>
            <p className="auth-terms">{t('workAccount')}</p>
          </form>

          <div className="auth-demo">
            <span>{t('haveAccount')}</span>
            <Link to="/login">{t('signIn')}</Link>
          </div>
          <div className="trial-alt">
            <span>{t('preferDemo')}</span>
            <Link to="/?demo=1">{t('bookDemo')}</Link>
          </div>
        </motion.section>
      </main>

      <footer className="auth-foot">
        <span>© {new Date().getFullYear()} Martinci Software &amp; Consulting</span>
        <nav aria-label={t('legalAria')}>
          {t('legalLinks').map(([to, label]) => <Link key={to} to={to}>{label}</Link>)}
        </nav>
      </footer>
    </div>
  );
};

export default StartTrialPage;
